import React, { useEffect, useState } from 'react';

import { Modal, Table } from '@components/common';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm';
import ImportMapDataContainer from '@components/ModalComponents/ImportMapDataContainer/ImportMapDataContainer';
import { getColumns } from '@components/routes/AdminPanel/ManageAllCells/AllCellsTab/allCellsColumns';

import { useMutateExportCells } from '@hooks/cells/useMutateExportCells';
import { useQueryCells } from '@hooks/cells/useQueryCells';
import { useManageCellOptions } from '@hooks/useManageCellOptions';
import { useManageSitesOptions } from '@hooks/useManageSitesOptions';

import { allCells as allCellsSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetAllSitesDBTable,
	setBand,
	setPage,
	setPageSize,
	setSearchValue,
	setSiteType,
	setSort,
} from '@slices/allCells';

import { setFilterUtil } from '@utils/filterUtil';

import { TExportAssetsDTO } from '@interfaces/assets/dto/assetsDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSort, TTableFilter } from '@interfaces/components/table';

import {
	ETechnologiesValues,
	EVendorsValues,
	TMenuActionsTypes,
	TTechnologiesValues,
	TVendorsValues,
} from '@enums/common';
import { EImportingDataType } from '@enums/dropzone';

import { exportRadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';
import {
	acceptedSitesFileTypes,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/UploadFile/constants';

import styles from '../ManageAllCells.module.scss';

interface IAllCellsTabProps {
	technologyValue: TTechnologiesValues;
	vendor: TVendorsValues;
}

const SEARCH_THRESHOLD = 0;

const getVendorTechnology = (technologyValue: TTechnologiesValues, vendor: TVendorsValues) => {
	const { T_5G, T_4G_FDD } = ETechnologiesValues;

	const commonProps = {
		is5G: technologyValue === T_5G,
		is4GFdd: technologyValue === T_4G_FDD,
	};

	return vendor !== EVendorsValues.limenet
		? { ...commonProps, exportKeys: ['cellId', 'siteId'] }
		: { ...commonProps, exportKeys: ['cellId', 'crowdCellId'] };
};

const AllCellsTab = ({
	technologyValue,
	vendor,
}: IAllCellsTabProps) => {
	const dispatch = useAppDispatch();

	const { is4GFdd, is5G, exportKeys } = getVendorTechnology(technologyValue, vendor);

	const [selectedBand, setSelectedBand] = useState<TSelectOption | null>(null);
	const [selectedSiteType, setSelectedSiteType] = useState<TSelectOption | null>(null);
	const [selectedCellsIds, setSelectedCellsIds] = useState<string[] | [] | null>(null);
	const [activeExportAction, setActiveExportAction] = useState<TMenuActionsTypes>();

	const [isImportSites, setIsImportSites] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const toggleImportSites = () => setIsImportSites(prevState => !prevState);

	const searchPlaceholder = vendor === EVendorsValues.limenet ? 'Search by site ID' : 'Search by SiteID/CellName/NodeId';

	useEffect(() => {
		dispatch(resetAllSitesDBTable());

		setSelectedSiteType(null);
		setSelectedBand(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [technologyValue, vendor]);

	const { siteTypesOptions } = useManageSitesOptions();

	const {
		band4GFddOptions,
		band4GTddOptions,
		band5GOptions,
	} = useManageCellOptions();

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		band,
		siteType,
		siteId,
		node,
	} = useAppSelector(allCellsSelector);

	const {
		allCells,
		cellsCount,
		isLoadingTable,
	} = useQueryCells({
		technologyValue,
		vendor,
		page,
		pageSize,
		searchValue,
		siteId,
		band,
		siteType,
		sortDirection,
		sortFieldName,
		node,
	});

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedSiteType,
			currentValue: siteType,
			setAction: setSiteType,
			fieldName: 'siteType',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSiteType]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedBand,
			currentValue: band,
			setAction: setBand,
			fieldName: 'band',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBand]);

	const filters: Array<TTableFilter> = [
		{
			name: 'siteType',
			placeholder: 'Site type',
			option: selectedSiteType,
			options: siteTypesOptions,
			minWidth: '200px',
			setState: setSelectedSiteType,
			defaultValue: siteType,
		},
		{
			name: 'band',
			placeholder: 'Band',
			option: selectedBand,
			options: is5G ? band5GOptions : is4GFdd ? band4GFddOptions : band4GTddOptions,
			minWidth: '200px',
			setState: setSelectedBand,
			defaultValue: band,
		},
	];

	const onSearchHandler = (searchValue: string) =>
		dispatch(setSearchValue({ searchValue }));

	const defaultSearch = [
		{
			initialValue: siteId,
			handler: onSearchHandler,
			tooltipText: searchPlaceholder,
			isNumber: vendor === EVendorsValues.limenet,
			name: 'siteId',
			label: 'Site ID',
		},
	];

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const pagination: ITablePagination = {
		countOfRecords: cellsCount,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const columns = getColumns({ is5G });

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const handleExport = (assetsIds: Array<any>, actionId: TMenuActionsTypes) => {
		setSelectedCellsIds(assetsIds);
		setActiveExportAction(actionId);
		toggleFileTypeSelectionModal();
	};

	const closeTypeSelectionForm = () => {
		setIsOpenFileTypeSelectionModal(false);
	};

	const {
		handlePreparingExportCellsByTechnology,
		isLoadingExportCellsByTechnology,
	} = useMutateExportCells();

	const handlePreparingExport = (dto: TExportAssetsDTO) => {
		handlePreparingExportCellsByTechnology({
			technologyValue,
			dto: {
				vendor,
				fileType: dto.fileType,
				ids: dto?.ids?.length ? {
					items: dto.ids,
				} : undefined,
				filter: dto?.filter ? {
					band: dto.filter?.band || null,
					[is5G ? 'gnodeBId' : 'enodeBId']: dto.filter?.node || null,
					page: dto.filter?.page,
					pageSize: dto.filter?.page,
					searchValue: dto.filter?.searchValue || null,
					siteId: +dto.filter?.siteId || null,
					siteType: dto.filter?.siteType,
					sortDirection: dto.filter?.sortDirection,
					sortFieldName: dto.filter?.sortFieldName,
					vendor: dto.filter?.vendor || null,
				} : null,
			},
		});
	};

	return (
		<>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionForm
					activeGroupTableAction={activeExportAction}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					filter={{
						page,
						pageSize,
						sortDirection,
						sortFieldName,
						searchValue,
						band,
						siteType,
						siteId,
						node,
						vendor,
					}}
					handlePreparingExport={handlePreparingExport}
					isLoading={isLoadingExportCellsByTechnology}
					selectedIds={selectedCellsIds}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			{isImportSites
				&& <ImportMapDataContainer
					acceptedFileTypes={acceptedSitesFileTypes}
					additionalParams={{ fieldsTechnology: technologyValue }}
					importType={EImportingDataType.site}
					importVendor={vendor}
					isShowImportModal={isImportSites}
					toggleImportModal={toggleImportSites}
				/>
			}
			<div className={styles.manageAllSitesContainer}>
				<Table
					columns={columns}
					data={allCells}
					exportTooltipText='Export sites/cells to file'
					filters={filters}
					handleExport={handleExport}
					handleImport={toggleImportSites}
					isExport
					isImportable
					isLoading={isLoadingTable}
					isWithoutActions
					isWithoutDeleteAction
					keysForCheckedIds={exportKeys}
					pagination={pagination}
					search={defaultSearch}
					searchThreshold={SEARCH_THRESHOLD}
					sort={sort}
				/>
			</div>
		</>
	);
};

export default AllCellsTab;
